// # Step 1 Mentorship Role Access Utility
// # This utility handles role-based access control for Step 1 mentorship components
import React from 'react';
import { Navigate } from 'react-router-dom';
import ProtectedRouteUser from '../../routes/protected/protected_route_user';
import ProtectedRoute from '../../routes/ProtectedRoute';

/**
 * # Determines the targeted user ID based on role context
 * @param {Object} params - Parameters object
 * @param {boolean} params.isAdmin - Whether the current user is an admin
 * @param {boolean} params.isTutor - Whether the current user is a tutor
 * @param {string} params.viewingMenteeId - ID of mentee being viewed (if any)
 * @param {string} params.currentUserId - ID of the current user
 * @returns {string} - The appropriate user ID to use for data operations
 */
export const getTargetUserId = ({ isAdmin, isTutor, viewingMenteeId, currentUserId }) => {
  // # Return mentee ID if admin/tutor is viewing a mentee, otherwise return current user ID
  return (isAdmin || isTutor) && viewingMenteeId ? viewingMenteeId : currentUserId;
};

/**
 * # Wraps component in appropriate protection based on user role
 * @param {Object} params - Parameters object
 * @param {React.Component} params.Component - The component to render
 * @param {boolean} params.isAdmin - Whether the current user is an admin
 * @param {boolean} params.isTutor - Whether the current user is a tutor
 * @param {Object} params.componentProps - Props to pass to the component
 * @returns {React.Element} - Protected component
 */
export const wrapWithProtection = ({ Component, isAdmin, isTutor, componentProps }) => {
  // # Admin access
  if (isAdmin) {
    return (
      <ProtectedRoute requiredRole="admin">
        <Component {...componentProps} isAdmin={true} />
      </ProtectedRoute>
    );
  }
  
  // # Tutor access
  if (isTutor) {
    return (
      <ProtectedRoute requiredRole="tutor">
        <Component {...componentProps} isTutor={true} />
      </ProtectedRoute>
    );
  }
  
  // # Regular user/mentee access
  return (
    <ProtectedRouteUser requiredSubscription="step1_mentorship">
      <Component {...componentProps} />
    </ProtectedRouteUser>
  );
};

/**
 * # Gets the base path based on user role and context
 * @param {Object} params - Parameters object
 * @param {boolean} params.isAdmin - Whether the current user is an admin
 * @param {boolean} params.isTutor - Whether the current user is a tutor
 * @param {boolean} params.isMentorView - Whether this is a mentor view
 * @param {string} params.username - Username of mentee being viewed (for mentor view)
 * @returns {string} - Appropriate base path
 */
export const getBasePath = ({ isAdmin, isTutor, isMentorView, username }) => {
  if (isMentorView && username) {
    if (isAdmin) {
      return `/mentor/step1/${username}`;
    }
    if (isTutor) {
      return `/tutor/mentorship/step1/${username}`;
    }
  }
  
  if (isAdmin) {
    return '/admin/mentorship';
  }
  
  if (isTutor) {
    return '/tutor/mentorship';
  }
  
  return '/mentorship';
};

/**
 * # Handles navigation when incorrect role access is attempted
 * @param {Object} params - Parameters object
 * @param {boolean} params.isAdmin - Whether the current user is an admin
 * @param {boolean} params.isTutor - Whether the current user is a tutor
 * @returns {React.Element|null} - Redirect component or null
 */
export const handleInvalidAccess = ({ isAdmin, isTutor }) => {
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }
  
  if (isTutor) {
    return <Navigate to="/tutor" replace />;
  }
  
  return <Navigate to="/dashboard" replace />;
};

/**
 * # Determines if the current user can edit content
 * @param {Object} params - Parameters object
 * @param {boolean} params.isAdmin - Whether the current user is an admin
 * @param {boolean} params.isTutor - Whether the current user is a tutor
 * @param {boolean} params.isMentorView - Whether this is a mentor view
 * @returns {boolean} - Whether the user can edit content
 */
export const canEditContent = ({ isAdmin, isTutor, isMentorView }) => {
  // # Admin and tutor can edit in mentor view
  return (isAdmin || isTutor) && isMentorView;
};

// # Export a single default object with all methods
export default {
  getTargetUserId,
  wrapWithProtection,
  getBasePath,
  handleInvalidAccess,
  canEditContent
};
